import { cleanFaceSprites, type FaceSprites } from "./faceStyles";

// Drawing styles: the words at the start of every painting prompt that say how it should look. A few come with the
// app; people can write their own, kept in the library. The image maker remembers the last one used. Shared by the
// server and browser.

// sample: a picture of SAMPLE_PROMPT painted in the style, to see what it looks like.
// faces: a custom style's face set (src/model/faceStyles.ts), once it has been made; ready-made ones are in art/faces.json.
export type PaintStyle = { id: string; name: string; text: string; sample?: string; faces?: FaceSprites; custom?: true };

// Every style's sample is this same picture, so they can be compared side by side.
export const SAMPLE_PROMPT = "A grown-up person standing on a sunny beach next to their dog, the sea and sky behind them.";
// The ready-made styles' samples are painted once by scripts/styleSamples.ts.
const presetSample = (id: string) => `/art/styles/${id}.webp`;

export const MAX_STYLE_NAME = 40;
export const MAX_STYLE_TEXT = 600;

// Approved 2026-10-17 from a test of three styles: rough scratchy ink, sickly limited palette, adult-cartoon pilot sketch.
// scripts/art.ts paints the face sprites and starter backgrounds in it, so a talking face matches it best.
export const SCRATCHY_PEN: PaintStyle = {
  id: "scratchy-pen",
  name: "Scratchy pen",
  sample: presetSample("scratchy-pen"),
  text:
    "Very rough hand-drawn adult cartoon, like an Adult Swim pilot sketch: frantic scratchy black ink pen lines drawn over and over, " +
    "wobbly crooked proportions, lines that overshoot and don't close, small ink blots. Flat colour in patches that slightly miss the lines. " +
    "Sickly limited palette: acid yellow-green, dirty pink, grey, black, with grimy smudges.",
};

export const PRESET_STYLES: PaintStyle[] = [
  SCRATCHY_PEN,
  {
    id: "clean-cartoon",
    sample: presetSample("clean-cartoon"),
    name: "Clean cartoon",
    text: "Clean modern TV cartoon: smooth confident black outlines of even weight, simple rounded shapes, flat bright colours with one soft shade, tidy and readable.",
  },
  {
    id: "rubber-hose",
    sample: presetSample("rubber-hose"),
    name: "Rubber hose",
    text: "1930s rubber-hose cartoon: bendy noodle limbs, pie-cut eyes, white gloves, thick black ink outlines, slightly yellowed paper, muted sepia and cream colours with film grain.",
  },
  {
    id: "crayon",
    sample: presetSample("crayon"),
    name: "Kid's crayon",
    text: "A child's crayon drawing on rough white paper: waxy uneven crayon strokes, colouring that goes over the lines, wonky shapes and big heads, bright primary colours.",
  },
  {
    id: "watercolour",
    sample: presetSample("watercolour"),
    name: "Storybook watercolour",
    text: "Gentle picture-book illustration: loose pencil lines under soft watercolour washes, colour bleeding at the edges, paper texture showing through, warm muted palette.",
  },
  {
    id: "anime",
    sample: presetSample("anime"),
    name: "Anime",
    text: "Japanese TV anime: crisp thin line art, cel shading with hard-edged shadows, expressive eyes, clean bright colours and painted backgrounds.",
  },
];

export const DEFAULT_STYLE = SCRATCHY_PEN;

// What the app sends as a style, checked: trimmed and cut to length. Anything unusable gives the default style's words.
export function styleText(text: unknown): string {
  const t = typeof text === "string" ? text.trim().slice(0, MAX_STYLE_TEXT) : "";
  return t || DEFAULT_STYLE.text;
}

// Custom styles as the app sends them, cleaned. Ones with no words are dropped.
export function cleanStyles(list: unknown): PaintStyle[] {
  if (!Array.isArray(list)) return [];
  return list.flatMap((s): PaintStyle[] => {
    if (!s || typeof s.id !== "string" || typeof s.text !== "string" || !s.text.trim()) return [];
    const name = (typeof s.name === "string" ? s.name : "").trim().slice(0, MAX_STYLE_NAME) || "My style";
    const sample = typeof s.sample === "string" && /^\/data\/v2\/images\/[a-z0-9_-]+\.(webp|png)$/i.test(s.sample) ? { sample: s.sample } : {};
    const faces = cleanFaceSprites(s.faces);
    return [{ id: s.id, name, text: s.text.trim().slice(0, MAX_STYLE_TEXT), ...sample, ...(faces ? { faces } : {}), custom: true }];
  });
}
